import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Package, Calendar, Tag, CheckCircle2, XCircle, FileText, ArrowRight,
  Search, FileSearch, Presentation, Sparkles,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ServiceHero, { Highlight } from "./ServiceHero";
import IncludesExcludes from "./IncludesExcludes";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}
function AnimateIn({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(30px)",
      transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>{children}</div>
  );
}

const includesList = [
  "Sesión 1 (60 min): diagnóstico y entendimiento de tu negocio con un experto",
  "Análisis completo de tu situación actual",
  "PDF entregable con el análisis y los pasos a seguir (en 7 días)",
  "Sesión 2 (45 min): explicación detallada del plan de acción, paso a paso",
  "Priorización de acciones: qué hacer primero y por qué",
];

const needsList = [
  "Información sobre tu negocio y tus objetivos",
  "Acceso o datos de tu marketing actual (métricas, cuentas, resultados)",
  "Contexto de lo que has hecho hasta ahora",
];

const notIncluded = [
  "Grabación de las sesiones",
  "Ejecución de las acciones recomendadas (eso son nuestros servicios de marketing)",
  "Sesiones de seguimiento adicionales",
  "Soporte continuo entre sesiones",
  "Implementación técnica",
];

const process = [
  { n: 1, icon: <Search className="w-6 h-6" />, title: "Sesión de diagnóstico", duration: "60 min", desc: "Un experto se reúne contigo para entender tu negocio a fondo: te hace las preguntas clave, conoce tu situación, tus objetivos y tus retos." },
  { n: 2, icon: <FileSearch className="w-6 h-6" />, title: "Análisis y plan", duration: "7 días", desc: "Elaboramos un análisis completo de tu situación y preparamos un PDF entregable con el diagnóstico y los pasos concretos a seguir." },
  { n: 3, icon: <Presentation className="w-6 h-6" />, title: "Sesión de plan de acción", duration: "45 min", desc: "Nos reunimos de nuevo para explicarte paso por paso el plan, resolver tus dudas y dejarte con una hoja de ruta clara para avanzar." },
];

const ConsultoriaPuntualDemo = () => {
  return (
    <div className="space-y-28 overflow-hidden">
      <ServiceHero
        eyebrow="Servicio puntual · Pago único"
        title={<>Consultoría <Highlight>Estratégica</Highlight></>}
        description={<>Una visión experta sobre tu negocio. Analizamos tu situación a fondo y te entregamos un plan claro con los pasos exactos que debes seguir para crecer.</>}
        includes={["320€ (IVA incl.) · Pago único", "Financiable en 3 plazos con Klarna", "2 sesiones + PDF con plan de acción"]}
        primaryCta={{ label: "Reservar mi consultoría", href: "/#contact" }}
        secondaryCta={{ label: "Cómo funciona", href: "#proceso" }}
      />

      <IncludesExcludes
        includes={[
          "Sesión 1 (60 min): diagnóstico y entendimiento de tu negocio",
          "Análisis completo de tu situación (en 7 días)",
          "PDF entregable con el análisis y los pasos a seguir",
          "Sesión 2 (45 min): explicación paso a paso del plan",
          "Priorización de acciones",
        ]}
        excludes={[
          "Grabación de las sesiones",
          "Ejecución de las acciones recomendadas",
          "Sesiones de seguimiento adicionales",
          "Soporte continuo entre sesiones",
          "Implementación técnica",
        ]}
      />

      {/* 3 COLUMNAS */}
      <section>
        <AnimateIn>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: <Package className="w-6 h-6" />, title: "Qué incluye", items: ["2 sesiones con un experto", "Análisis completo de tu negocio", "PDF con plan de acción", "Pasos a seguir detallados"] },
              { icon: <Calendar className="w-6 h-6" />, title: "Plazo", items: ["PDF entregable en 7 días tras la primera sesión", "Segunda sesión tras la entrega"] },
              { icon: <Tag className="w-6 h-6" />, title: "Inversión", items: ["320€ (IVA incluido)", "Pago único"] },
            ].map((c, i) => (
              <Card key={i} className="p-6 hover:border-primary/30 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">{c.icon}</div>
                <h4 className="font-semibold mb-3">{c.title}</h4>
                <ul className="space-y-2">
                  {c.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 size={14} className="text-primary shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </AnimateIn>
      </section>

      {/* CÓMO FUNCIONA */}
      <section id="proceso">
        <AnimateIn>
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Proceso</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Cómo funciona tu consultoría</h3>
            <p className="text-muted-foreground">La consultoría se desarrolla en tres pasos para darte un análisis real y un plan que puedas ejecutar de verdad.</p>
          </div>
        </AnimateIn>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto relative">
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20" />
          {process.map((p, i) => (
            <AnimateIn key={p.n} delay={i * 0.1}>
              <Card className="relative p-6 h-full bg-card hover:border-primary/40 hover:shadow-glow transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground flex items-center justify-center font-bold">
                    {p.n}
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    {p.icon}
                  </div>
                </div>
                <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">{p.duration}</div>
                <h4 className="text-lg font-bold mb-2">{p.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </Card>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* INCLUYE */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Alcance del servicio</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Todo lo que incluye</h3>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-3">
          {includesList.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.03}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border h-full">
                <CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5" />
                <span className="text-sm">{item}</span>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* PARA QUIÉN ES */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto rounded-3xl p-8 md:p-12 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30 shadow-glow">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <Sparkles className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold mb-3">Ideal si no sabes por dónde empezar</h3>
                <p className="text-muted-foreground mb-3">
                  Esta consultoría es perfecta si sientes que tu marketing no avanza, si no sabes dónde invertir tu tiempo y tu dinero, o si quieres una <strong className="text-foreground">visión externa y experta</strong> antes de tomar decisiones.
                </p>
                <p className="text-muted-foreground">
                  Sales con un plan claro y unos pasos concretos. <strong className="text-foreground">Tú decides si los ejecutas por tu cuenta o si quieres que te ayudemos a hacerlo.</strong>
                </p>
              </div>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* QUÉ NECESITAMOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Antes de empezar</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Qué necesitamos para empezar</h3>
            <p className="text-muted-foreground">Para que la consultoría sea lo más útil posible, necesitamos que nos facilites:</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto space-y-3">
          {needsList.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.04}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <FileText size={16} />
                </div>
                <span className="text-sm pt-1">{item}</span>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* QUÉ NO INCLUYE */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="outline" className="mb-4 border-destructive/40 text-destructive">Sin sorpresas</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Qué no incluye este servicio</h3>
            <p className="text-muted-foreground">Para que todo quede claro desde el principio, estos puntos quedan fuera del servicio:</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto grid gap-3">
          {notIncluded.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.04}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                <XCircle size={18} className="text-destructive shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">{item}</span>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto text-center rounded-3xl p-10 md:p-16 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30 shadow-glow">
            <h3 className="text-3xl md:text-5xl font-bold mb-4">¿Listo para tener un plan claro?</h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Pago único de 320€, financiable en 3 plazos con Klarna. Dos sesiones con un experto y un plan de acción a tu medida en unos 12 días.
            </p>
            <a href="/#contact" className="btn-pill bg-primary text-primary-foreground hover:shadow-glow px-8 py-4 inline-flex items-center gap-2">
              Reservar mi consultoría
              <ArrowRight size={16} />
            </a>
          </div>
        </AnimateIn>
      </section>
    </div>
  );
};

export default ConsultoriaPuntualDemo;
